/**
 * "Import from a tester": a CSV / TXT / Excel export of an installation tester read into the
 * test results (@elec/export instruments.ts). The preview shows which column was taken for each
 * value and which circuit or RCD each row goes to; both can be corrected before recording.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Analysis } from '@elec/analysis';
import type { Op, Project } from '@elec/domain';
import type { ColumnMap, InstrumentField, InstrumentFile, InstrumentTargets, Match } from '@elec/export';
import type { MessageKey, Translate } from '@elec/i18n';
import { measurementTargets } from '@elec/rules';
import { Dialog } from './ui.tsx';

const today = () => new Date().toISOString().slice(0, 10);
const FIELD_LABEL: Record<InstrumentField, MessageKey> = {
  ref: 'instr.fieldRef',
  insulationMOhm: 'instr.fieldInsulation',
  testVoltageV: 'instr.fieldTestVoltage',
  continuityOhm: 'instr.fieldContinuity',
  loopOhm: 'instr.fieldLoop',
  tripMs: 'instr.fieldTripMs',
  tripMa: 'instr.fieldTripMa',
  date: 'instr.fieldDate',
};
const UNIT: Partial<Record<InstrumentField, string>> = {
  insulationMOhm: 'MΩ',
  testVoltageV: 'V',
  continuityOhm: 'Ω',
  loopOhm: 'Ω',
  tripMs: 'ms',
  tripMa: 'mA',
};

export function instrumentTargets(a: Analysis): InstrumentTargets {
  const { circuits, rcds } = measurementTargets(a);
  return {
    circuits: [...circuits].map(([code, c]) => ({
      code,
      label: c.circuit?.label ?? '',
      ...(c.rcds[0]?.tag ? { rcd: c.rcds[0].tag } : {}),
    })),
    rcds: [...rcds.keys()],
  };
}

/** The button of the measurements dialog: pick a file, then the preview. */
export function InstrumentImportButton(props: {
  project: Project;
  analysis: Analysis;
  t: Translate;
  readOnly: boolean;
  dispatch(label: string, ops: Op[]): void;
  notify?(msg: string): void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<{ name: string; bytes: Uint8Array } | null>(null);
  return (
    <>
      <button onClick={() => input.current?.click()} disabled={props.readOnly} data-testid="instr-import">
        {props.t('instr.button')}
      </button>
      <input
        ref={input}
        type="file"
        accept=".csv,.txt,.tsv,.xlsx"
        hidden
        data-testid="instr-file"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) setFile({ name: f.name, bytes: new Uint8Array(await f.arrayBuffer()) });
        }}
      />
      {file && <InstrumentImportDialog {...props} file={file} onClose={() => setFile(null)} />}
    </>
  );
}

function InstrumentImportDialog(props: {
  project: Project;
  analysis: Analysis;
  t: Translate;
  file: { name: string; bytes: Uint8Array };
  dispatch(label: string, ops: Op[]): void;
  notify?(msg: string): void;
  onClose(): void;
}) {
  const { t } = props;
  const targets = useMemo(() => instrumentTargets(props.analysis), [props.analysis]);
  const [x, setX] = useState<typeof import('@elec/export') | null>(null);
  const [columns, setColumns] = useState<Partial<ColumnMap>>({});
  const [matches, setMatches] = useState<Map<number, Match>>(new Map());
  useEffect(() => {
    void import('@elec/export').then(setX);
  }, []);
  const read: InstrumentFile | null = useMemo(() => {
    if (!x) return null;
    try {
      return x.readInstrumentFile(props.file.name, props.file.bytes, targets, columns);
    } catch {
      return null;
    }
  }, [x, columns, targets]);
  const rows = (read?.rows ?? []).map((r) =>
    matches.has(r.line) ? { ...r, match: matches.get(r.line)! } : r,
  );
  const matched = rows.filter((r) => r.match).length;
  const record = () => {
    if (!x || !read) return;
    const r = x.instrumentOps(props.project, rows, targets, today());
    props.dispatch(t('instr.tx', { n: matched }), r.ops);
    props.notify?.(t('instr.done', { c: r.circuits.join(', ') || '—', r: r.rcds.join(', ') || '—' }));
    props.onClose();
  };
  const option = (m: Match) => (m ? `${m.target}:${m.ref}` : '');
  const fmt = (v: number | undefined) => (v === undefined ? '' : String(v).replace('.', ','));
  return (
    <Dialog
      title={t('instr.title', { file: props.file.name })}
      wide
      onClose={props.onClose}
      actions={
        <>
          <span className="muted small grow" data-testid="instr-summary">
            {read ? t('instr.summary', { n: rows.length, m: matched }) : t('instr.unreadable')}
          </span>
          <button onClick={props.onClose}>{t('dialog.cancel')}</button>
          <button className="primary" disabled={!matched} onClick={record} data-testid="instr-record">
            {t('instr.record', { n: matched })}
          </button>
        </>
      }
    >
      {read && (
        <>
          <div className="instr-columns">
            {(Object.keys(FIELD_LABEL) as InstrumentField[]).map((f) => (
              <label key={f} className="small">
                {t(FIELD_LABEL[f])}
                {UNIT[f] ? ` (${UNIT[f]})` : ''}
                <select
                  value={read.columns[f]}
                  onChange={(e) => setColumns((c) => ({ ...c, [f]: Number(e.target.value) }))}
                  data-testid={`instr-col-${f}`}
                >
                  <option value={-1}>—</option>
                  {read.header.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `#${i + 1}`}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <div className="instr-table">
            <table data-testid="instr-rows">
              <thead>
                <tr>
                  <th>{t('instr.line')}</th>
                  <th>{t('instr.fieldRef')}</th>
                  <th>{t('instr.goesTo')}</th>
                  {(['insulationMOhm', 'continuityOhm', 'loopOhm', 'tripMs', 'tripMa'] as const).map((f) => (
                    <th key={f}>{UNIT[f]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.line} className={r.match ? '' : 'muted'}>
                    <td>{r.line}</td>
                    <td>{r.location}</td>
                    <td>
                      <select
                        value={option(r.match)}
                        onChange={(e) => {
                          const [target, ref] = e.target.value.split(':');
                          setMatches((m) =>
                            new Map(m).set(r.line, ref ? { target: target as 'circuit' | 'rcd', ref } : null),
                          );
                        }}
                        data-testid={`instr-match-${r.line}`}
                      >
                        <option value="">{t('instr.skip')}</option>
                        {targets.circuits.map((c) => (
                          <option key={c.code} value={`circuit:${c.code}`}>
                            {c.code} {c.label}
                          </option>
                        ))}
                        {targets.rcds.map((tag) => (
                          <option key={tag} value={`rcd:${tag}`}>
                            {tag}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{fmt(r.values.insulationMOhm)}</td>
                    <td>{fmt(r.values.continuityOhm)}</td>
                    <td>{fmt(r.values.loopOhm)}</td>
                    <td>{fmt(r.values.tripMs)}</td>
                    <td>{fmt(r.values.tripMa)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted small">{t('instr.hint')}</p>
        </>
      )}
    </Dialog>
  );
}
